import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mountain, ArrowLeft, MapPin, Calendar, Users } from 'lucide-react';
import FEATURED_DATA from '../data/featuredStayData';
import SEOHead from '../components/SEOHead';
import StayVitals from '../components/StayVitals';
import SimilarStays from '../components/SimilarStays';
import EnquireModal from '../components/EnquireModal';
import { trackLead } from '../utils/trackLead';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate slug from display_name — must match DiscoveredStays logic
export function toFeaturedSlug(name) {
  if (!name) return null;
  return name.split(',')[0].trim().toLowerCase()
    .replace(/['']/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function DiscoveredBadge() {
  return (
    <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg" style={{boxShadow: '0 2px 8px rgba(0,0,0,0.08)'}}>
      <svg className="w-4 h-4 text-[#2C5F8D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
      </svg>
      <span className="text-[10px] font-semibold text-stone-600 tracking-[0.15em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Discovered by GoHimalayan</span>
    </div>
  );
}

function FeaturedBadge({ label = 'Featured' }) {
  return (
    <div className="inline-flex items-center">
      <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg" style={{boxShadow: '0 2px 8px rgba(0,0,0,0.08)'}}>
        <svg className="w-4 h-4 text-[#2C5F8D] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
        </svg>
        <span className="text-[10px] font-semibold text-stone-600 tracking-[0.15em] uppercase" style={{fontFamily: 'Georgia, serif'}}>{label}</span>
      </div>
    </div>
  );
}

function ValleyPartnerCTABadge() {
  return (
    <div className="inline-flex items-center">
      <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg" style={{boxShadow: '0 2px 8px rgba(0,0,0,0.08)'}}>
        <svg className="w-4 h-4 text-[#3a5a40] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
        </svg>
        <span className="text-[10px] font-semibold text-stone-600 tracking-[0.15em] uppercase" style={{fontFamily: 'Georgia, serif'}}>Valley Partner</span>
      </div>
    </div>
  );
}

function CollectiveBadge() {
  return (
    <div className="inline-flex items-center">
      <div className="relative flex items-center bg-[#3a5a40]/90 backdrop-blur-sm px-4 py-2 rounded-lg" style={{boxShadow: '0 2px 8px rgba(0,0,0,0.08)'}}>
        <span
          className="absolute right-2.5 font-bold text-white/15 leading-none select-none"
          style={{fontSize: '44px', letterSpacing: '-2px'}}
        >100</span>
        <svg className="w-4 h-4 text-white/95 mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
        </svg>
        <div className="relative z-10 flex flex-col leading-tight">
          <span className="text-[11px] font-semibold text-white tracking-wide" style={{fontFamily: 'Georgia, serif'}}>GoHimalayan</span>
          <span className="text-[8px] font-medium text-white/70 tracking-[0.22em] uppercase">Collective</span>
        </div>
      </div>
    </div>
  );
}

// Light "Collective 100" variant — used on the dark blue CTA panel where the
// green-on-blue clash looks rough. Keeps the same brand mark, white pill instead.
function CollectiveBadgeLight() {
  return (
    <div className="inline-flex items-center">
      <div className="relative flex items-center bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg" style={{boxShadow: '0 2px 8px rgba(0,0,0,0.08)'}}>
        <span
          className="absolute right-2.5 font-bold text-[#3a5a40]/15 leading-none select-none"
          style={{fontSize: '44px', letterSpacing: '-2px'}}
        >100</span>
        <svg className="w-4 h-4 text-[#3a5a40] mr-2 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
        </svg>
        <div className="relative z-10 flex flex-col leading-tight">
          <span className="text-[11px] font-semibold text-stone-700 tracking-wide" style={{fontFamily: 'Georgia, serif'}}>GoHimalayan</span>
          <span className="text-[8px] font-medium text-stone-500 tracking-[0.22em] uppercase">Collective</span>
        </div>
      </div>
    </div>
  );
}

// Smart badge — chooses the right credential based on the stay's partnership_status.
// • featured            → "Featured" star (top-tier curation, hero placement)
// • partner             → "GoHimalayan Collective" badge (green on hero / white on dark CTA)
// • valley_partner      → "Valley Partner" (regional collaboration)
// • everything else     → "Discovered by GoHimalayan"
// `variant`: 'hero' (default — over image) | 'cta' (over the dark-blue CTA panel)
function StayBadge({ partnershipStatus, variant = 'hero' }) {
  if (partnershipStatus === 'featured') {
    return <FeaturedBadge />;
  }
  if (partnershipStatus === 'partner') {
    return variant === 'cta' ? <CollectiveBadgeLight /> : <CollectiveBadge />;
  }
  if (partnershipStatus === 'valley_partner') {
    return <ValleyPartnerCTABadge />;
  }
  return <DiscoveredBadge />;
}

const getMonthName = (m) => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m - 1];
const getBestMonths = (months) => {
  if (!months || months.length === 0) return 'Year-round';
  if (months.length > 6) return 'Most of the year';
  return months.map(getMonthName).join(', ');
};

const prettifyTag = (t) => {
  if (!t) return '';
  const map = {
    riverside: 'Riverside', forest: 'Forest', mountain_view: 'Mountain View',
    orchard: 'Orchard', orchards: 'Orchards', heritage: 'Heritage', wildlife: 'Wildlife',
    adventure: 'Adventure', mountain_retreat: 'Mountain Retreat', spiritual: 'Spiritual',
    trekking: 'Trekking', river: 'Riverside', birding: 'Birding', wellness: 'Wellness',
    food_forward: 'Food Forward', fishing: 'Fishing', lake: 'Lakeside',
    paragliding: 'Paragliding', trekking_gateway: 'Trekking Gateway',
    annapurna_view: 'Annapurna View', monasteries: 'Monastery Trails',
    stream: 'Stream-side', secluded: 'Secluded', peaceful: 'Peaceful',
    luxury: 'Luxury', stargazing: 'Stargazing', culinary: 'Culinary',
    high_altitude: 'High Altitude', photography: 'Photography', hiking: 'Hiking'
  };
  if (map[t]) return map[t];
  return String(t).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getExpLabel = (types) => {
  if (!types || types.length === 0) return 'Mountain Stay';
  return types.slice(0, 3).map(prettifyTag).join(' · ');
};

export default function FeaturedStay() {
  const { slug } = useParams();
  const [stay, setStay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquireOpen, setEnquireOpen] = useState(false);

  const fileContent = FEATURED_DATA[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    async function fetchStay() {
      try {
        const res = await fetch(`${API}/api/curated/stays?limit=200`);
        const data = await res.json();
        const all = data.stays || [];
        const match = all.find(s => toFeaturedSlug(s.display_name) === slug);
        if (match) setStay(match);
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    fetchStay();
  }, [slug]);

  // Build fallback content from DB when no hand-written editorial exists.
  // Newly curated stays (via /admin/review → AI Draft → Publish) live entirely
  // in MongoDB — `storyline` or `description` from the curation pipeline.
  const dbContent = stay && (stay.storyline || stay.description) ? (() => {
    const paragraphs = (stay.storyline || stay.description || '')
      .split(/\n\s*\n/)
      .map(s => s.trim())
      .filter(Boolean);

    // Derive a closing punchline. Priority:
    //   1. Explicit `closing_line` curated on the stay (preferred)
    //   2. Last 1–2 sentences of the storyline (cuts a clean editorial close)
    //   3. Generic discover-on-GH fallback
    // If we DERIVE from the story, we also strip those sentences from the
    // last paragraph so they don't render twice (once in body, once in bold).
    let paragraphsForBody = paragraphs;
    const deriveClosingFromStory = () => {
      const fullText = paragraphs.join(' ');
      if (!fullText) return null;
      const sentences = fullText
        .split(/(?<=[.!?])\s+/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
      if (!sentences.length) return null;
      const tail = sentences.slice(-2).join(' ');
      const tailClean = tail.replace(/^[—–-]\s*/, '').replace(/^welcome to.*?$/i, '').trim() || sentences[sentences.length - 1];

      // Remove the tail sentences from the last body paragraph so we don't
      // repeat them below in the closing-line callout.
      if (paragraphs.length && tailClean) {
        const last = paragraphs[paragraphs.length - 1];
        const trimmed = last.replace(tailClean, '').replace(/\s{2,}/g, ' ').trim();
        paragraphsForBody = [...paragraphs.slice(0, -1), ...(trimmed ? [trimmed] : [])];
      }
      return tailClean;
    };

    const closingLine = stay.closing_line
      || deriveClosingFromStory()
      || `Discover ${stay.display_name?.trim()} on GoHimalayan.`;

    return {
      headline: stay.tagline || stay.display_name,
      story: paragraphsForBody,
      closingLine,
    };
  })() : null;

  const content = fileContent || dbContent;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf8f4]">
        <div className="animate-pulse text-stone-400 text-sm">Loading...</div>
      </div>
    );
  }

  if (!content || !stay) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf8f4]">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Page Not Found | GoHimalayan</title>
        </Helmet>
        <div className="text-center">
          <Mountain className="w-10 h-10 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-400 text-sm mb-6">Stay not found</p>
          <Link to="/discovered" className="text-stone-600 underline text-sm">Back to Stays</Link>
        </div>
      </div>
    );
  }

  const { headline, story, closingLine } = content;
  // Normalize photo URLs — older submissions saved photos as absolute URLs pointing
  // to the preview domain (alpine-rentals-7.emergent.host). Strip that prefix so the
  // browser fetches them same-origin from whatever domain it's loading the page on.
  const normalizeUrl = (u) => {
    if (!u) return u;
    if (u.startsWith('https://alpine-rentals-7.emergent.host')) {
      return u.replace('https://alpine-rentals-7.emergent.host', '');
    }
    if (u.startsWith('https://homestead-partner.preview.emergentagent.com')) {
      return u.replace('https://homestead-partner.preview.emergentagent.com', '');
    }
    return u;
  };

  const heroImage = normalizeUrl((stay.images || []).find(i => (i?.section === 'hero')) ?.url || stay.images?.[0]?.url);
  const galleryImages = stay.images?.slice(1).map(img => normalizeUrl(img.url)).filter(Boolean) || [];
  const displayName = stay.display_name;

  // Section-aware grouping (used when images have explicit `section` tags — e.g. Banpravas).
  // Falls back to legacy index-based layout below when no sections present.
  const sectionedImages = (stay.images || [])
    .filter(img => img && img.section && img.section !== 'hero')
    .map(img => ({ ...img, url: normalizeUrl(img.url) }));
  // Use sectioned layout only when there's MORE than one section — otherwise fall back to
  // the showcase grid that displays ALL photos (avoids the "only 4 of 15 visible" bug).
  const distinctSections = new Set(sectionedImages.map(i => i.section));
  const useSectionedLayout = distinctSections.size > 1;
  const groupedSections = useSectionedLayout
    ? sectionedImages.reduce((acc, img) => {
        if (!acc[img.section]) acc[img.section] = [];
        acc[img.section].push(img);
        return acc;
      }, {})
    : null;
  const SECTION_LABELS = {
    property: 'The Property',
    aerial: 'From Above',
    surroundings: 'The Surroundings',
    experience: 'The Surroundings',
    rooms: 'The Rooms',
    common: 'The Common Spaces',
    orchard: 'The Orchard',
    'kitchen-garden': 'The Kitchen Garden',
    landscape: 'The Landscape',
    table: 'Food & Delicacies',
    food: 'Food & Delicacies',
  };
  const SECTION_ORDER = ['property', 'aerial', 'surroundings', 'experience', 'rooms', 'common', 'orchard', 'kitchen-garden', 'landscape', 'table', 'food'];

  const BOOKING_PHONES = {
    hushstays: '918826915266',
    itmenaan: '917840077773',
    seclude: '919810743242',
    banjara: '919599481133',
    bluehouse: '919891355963',
    eaglenest: '919816423344',
    homestead: '918445439374',
    tra: '918851657804',
    mistymountains: '919927039123',
    chirpingchidiya: '919990100725',
    banpravas: '919415006854',
    gohimalayan: '919871155963',
  };
  const getWhatsappNumber = () => {
    const source = stay.booking_source;
    if (BOOKING_PHONES[source]) return BOOKING_PHONES[source];
    const name = (stay.display_name || '').toLowerCase();
    if (name.includes('bluehouse')) return BOOKING_PHONES.bluehouse;
    if (name.includes('eagle')) return BOOKING_PHONES.eaglenest;
    if (name.includes('tra boutique')) return BOOKING_PHONES.tra;
    if (name.includes('himalayan homestead')) return BOOKING_PHONES.homestead;
    if (name.includes('misty mountains')) return BOOKING_PHONES.mistymountains;
    // Use the host's WhatsApp from the application form (curated-via-/apply stays)
    const hostWa = (stay.whatsapp_number || '').replace(/[^0-9]/g, '');
    if (hostWa) return hostWa;
    return BOOKING_PHONES.gohimalayan;
  };
  const whatsapp = getWhatsappNumber();
  const whatsappMessage = `Hi, I found ${displayName} on GoHimalayan and would love to book a stay. Could you help me with availability and rates?`;

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="featured-stay-page">
      <SEOHead
        title={`${displayName} — ${headline}`}
        description={story[0]?.substring(0, 160)}
        image={heroImage}
        url={`/stay/${slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          "name": displayName,
          "description": story[0],
          "image": heroImage,
          "url": `https://gohimalayan.com/stay/${slug}`,
          "address": {
            "@type": "PostalAddress",
            "addressRegion": stay.region,
            "addressCountry": "IN"
          },
          ...(stay.altitude_meters && { "geo": { "@type": "GeoCoordinates", "elevation": stay.altitude_meters } }),
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.5",
            "bestRating": "5",
            "ratingCount": "10"
          }
        }}
      />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&display=swap" rel="stylesheet" />

      {/* Sticky Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#faf8f4]/90 backdrop-blur-md border-b border-stone-200/40">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
          <Link to="/discovered" className="flex items-center gap-2 text-stone-500 hover:text-stone-800 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs tracking-wider uppercase">Back</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <Mountain className="h-5 w-5 text-[#2C5F8D]" strokeWidth={2.5} />
            <span className="text-sm font-bold tracking-tight text-stone-800" style={{fontFamily: 'Playfair Display, serif'}}>GoHimalayan</span>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[85vh] min-h-[600px]" data-testid="hero-section">
        <div className="absolute inset-0">
          {heroImage ? (
            <img src={heroImage} alt={displayName} loading="eager" decoding="sync" fetchpriority="high" className="w-full h-full object-cover img-enhance" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#2C5F8D]/30 to-stone-300" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-black/10" />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end max-w-6xl mx-auto px-6 md:px-12 pb-20">
          <div className="mb-6">
            <StayBadge partnershipStatus={stay.partnership_status} />
          </div>
          <p className="text-white/50 text-[11px] tracking-[0.4em] uppercase mb-5">{stay.valley}, {stay.state}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight mb-4 font-light italic" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            {headline}
          </h1>
          <p className="text-white/70 text-lg max-w-xl font-light" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            {displayName}
          </p>
          <div className="flex flex-wrap gap-6 sm:gap-10 mt-10 text-white/70 text-xs sm:text-sm tracking-[0.15em] uppercase font-medium">
            {stay.altitude_meters && <span>{stay.altitude_meters.toLocaleString()}m</span>}
            <span>{getExpLabel(stay.experience_types)}</span>
            {stay.best_months && <span>{getBestMonths(stay.best_months)}</span>}
          </div>
        </div>
      </section>

      {/* White content panel */}
      <div className="bg-white mx-4 md:mx-8 lg:mx-16 -mt-8 relative z-20 rounded-t-2xl shadow-[0_-8px_40px_rgba(0,0,0,0.06)]" data-testid="content-panel">

        {/* Stay Vitals — at-a-glance fact strip */}
        <StayVitals stay={stay} />

        {/* Story */}
        <section className="max-w-3xl mx-auto px-6 md:px-12 pt-20 pb-16 text-center" data-testid="story-section">
          <div className="w-12 h-[1px] bg-stone-300 mb-12 mx-auto" />
          {story.map((para, i) => (
            <div key={i}>
              {i > 0 && <div className="w-16 h-[1px] bg-stone-200 mx-auto my-10" />}
              <p className="text-stone-600 text-lg md:text-xl leading-[1.9] font-light italic" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
                {para}
              </p>
            </div>
          ))}
        </section>

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="w-full h-[1px] bg-stone-200" />
        </div>

        {/* Photo Gallery — editorial sections matching Collective style */}
        {useSectionedLayout && (
          <section className="py-16" data-testid="gallery-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
              {SECTION_ORDER.filter(key => groupedSections[key] && groupedSections[key].length > 0).map((key, sectionIdx) => {
                const imgs = groupedSections[key];
                const isLast = sectionIdx === SECTION_ORDER.filter(k => groupedSections[k] && groupedSections[k].length > 0).length - 1;
                const spacing = isLast ? '' : 'mb-16';
                // Single image — full-width feature
                if (imgs.length === 1) {
                  return (
                    <div key={key} className={spacing}>
                      <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">{SECTION_LABELS[key] || key}</p>
                      <div className="group relative overflow-hidden rounded-lg">
                        <img src={imgs[0].url} alt={imgs[0].alt || `${displayName} ${key}`} loading="lazy" decoding="async" className="w-full h-[360px] md:h-[600px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      </div>
                    </div>
                  );
                }
                // Two images — duo grid
                if (imgs.length === 2) {
                  return (
                    <div key={key} className={spacing}>
                      <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">{SECTION_LABELS[key] || key}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {imgs.map((img, i) => (
                          <div key={i} className="group relative overflow-hidden rounded-lg">
                            <img src={img.url} alt={img.alt || `${displayName} ${key} ${i + 1}`} loading="lazy" decoding="async" className="w-full h-[340px] md:h-[480px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                // Exactly 3 — trio grid
                if (imgs.length === 3) {
                  return (
                    <div key={key} className={spacing}>
                      <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">{SECTION_LABELS[key] || key}</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {imgs.slice(0, 3).map((img, i) => (
                          <div key={i} className="group relative overflow-hidden rounded-lg">
                            <img src={img.url} alt={img.alt || `${displayName} ${key} ${i + 1}`} loading="lazy" decoding="async" className="w-full h-[300px] md:h-[420px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                const isCloseUpSection = key === 'table' || key === 'food';
                // Food/table sections: single full-width banner + smaller grid below.
                // Close-up macro food shots get cropped poorly at large sizes; this
                // layout lets the hero food shot (e.g. breakfast on the lawn) own
                // the section while the supporting close-ups sit smaller below.
                if (isCloseUpSection && imgs.length >= 2) {
                  return (
                    <div key={key} className={spacing}>
                      <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">{SECTION_LABELS[key] || key}</p>
                      <div className="group relative overflow-hidden rounded-lg mb-3">
                        <img src={imgs[0].url} alt={imgs[0].alt || `${displayName} ${key} 1`} loading="lazy" decoding="async" style={{objectPosition: 'center 70%'}} className="w-full h-[280px] md:h-[420px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {imgs.slice(1).map((img, i) => (
                          <div key={`food-${i}`} className="group relative overflow-hidden rounded-lg">
                            <img src={img.url} alt={img.alt || `${displayName} ${key} ${i + 2}`} loading="lazy" decoding="async" className="w-full h-[160px] md:h-[220px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                // Four or more — 2x2 grid for first 4, then 3-col overflow grid for the rest
                return (
                  <div key={key} className={spacing}>
                    <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">{SECTION_LABELS[key] || key}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {imgs.slice(0, 4).map((img, i) => (
                        <div key={i} className="group relative overflow-hidden rounded-lg">
                        <img src={img.url} alt={img.alt || `${displayName} ${key} ${i + 1}`} loading="lazy" decoding="async" className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                        </div>
                      ))}
                    </div>
                    {imgs.length > 4 && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                        {imgs.slice(4).map((img, i) => (
                          <div key={`overflow-${i}`} className="group relative overflow-hidden rounded-lg">
                            <img src={img.url} alt={img.alt || `${displayName} ${key} ${i + 5}`} loading="lazy" decoding="async" className="w-full h-[200px] md:h-[300px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Photo Gallery — editorial sections matching Collective style */}
        {!useSectionedLayout && galleryImages.length > 0 && (
          <section className="py-16" data-testid="gallery-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">

              {/* Section 1: THE PROPERTY — duo layout */}
              {galleryImages.length >= 2 && (
                <>
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">The Property</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-16">
                    {galleryImages.slice(0, 2).map((img, i) => (
                      <div key={i} className="group relative overflow-hidden rounded-lg">
                        <img src={img} alt={`${displayName} property ${i + 1}`} className="w-full h-[300px] md:h-[420px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Section 2: THE SURROUNDINGS — full-width cinematic */}
              {galleryImages.length >= 3 && (
                <>
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">The Surroundings</p>
                  <div className="mb-16">
                    <div className="group relative overflow-hidden rounded-lg">
                      <img src={galleryImages[2]} alt={`${displayName} surroundings`} className="w-full h-[350px] md:h-[520px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    </div>
                  </div>
                </>
              )}

              {/* Section 3: THE ROOMS — trio layout */}
              {galleryImages.length >= 6 && (
                <>
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">The Rooms</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-16">
                    {galleryImages.slice(3, 6).map((img, i) => (
                      <div key={i} className="group relative overflow-hidden rounded-lg">
                        <img src={img} alt={`${displayName} room ${i + 1}`} className="w-full h-[240px] md:h-[300px] object-cover transition-transform duration-1000 group-hover:scale-[1.03] img-enhance" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Section 4: THE COMMON SPACES — single feature image for indoor lounge/living */}
              {galleryImages.length >= 7 && (
                <>
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">The Common Spaces</p>
                  <div className="group relative overflow-hidden rounded-lg">
                    <img src={galleryImages[6]} alt={`${displayName} living room`} className="w-full h-[300px] md:h-[480px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  </div>
                </>
              )}

              {/* Section 5: THE LANDSCAPE — duo layout for remaining outdoor shots */}
              {galleryImages.length >= 9 && (
                <>
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">The Landscape</p>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="md:col-span-3 group relative overflow-hidden rounded-lg">
                      <img src={galleryImages[7]} alt={`${displayName} landscape`} className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    </div>
                    <div className="md:col-span-2 group relative overflow-hidden rounded-lg">
                      <img src={galleryImages[8]} alt={`${displayName} view`} className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    </div>
                  </div>
                </>
              )}

              {/* Section 6: AT THE TABLE — duo layout for food/kitchen shots */}
              {galleryImages.length >= 11 && (
                <>
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">At the Table</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="group relative overflow-hidden rounded-lg">
                      <img src={galleryImages[9]} alt={`${displayName} kitchen`} className="w-full h-[300px] md:h-[420px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    </div>
                    <div className="group relative overflow-hidden rounded-lg">
                      <img src={galleryImages[10]} alt={`${displayName} home-cooked meal`} className="w-full h-[300px] md:h-[420px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    </div>
                  </div>
                </>
              )}

              {/* Fallback for properties with 3-5 images */}
              {galleryImages.length >= 3 && galleryImages.length < 6 && (
                <>
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">The Rooms</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {galleryImages.slice(2).map((img, i) => (
                      <div key={i} className="group relative overflow-hidden rounded-lg">
                        <img src={img} alt={`${displayName} ${i + 3}`} className="w-full h-[260px] md:h-[340px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Fallback for 6-7 images */}
              {galleryImages.length >= 6 && galleryImages.length < 8 && (
                <>
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">The Landscape</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {galleryImages.slice(5).map((img, i) => (
                      <div key={i} className="group relative overflow-hidden rounded-lg">
                        <img src={img} alt={`${displayName} ${i + 6}`} className="w-full h-[260px] md:h-[340px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Overflow: remaining photos beyond the 11 hardcoded slots — 3-col masonry */}
              {galleryImages.length > 11 && (
                <div className="mt-16">
                  <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-6 font-medium">More from the property</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {galleryImages.slice(11).map((img, i) => (
                      <div key={i} className="group relative overflow-hidden rounded-lg">
                        <img src={img} alt={`${displayName} ${i + 12}`} loading="lazy" decoding="async" className="w-full h-[200px] md:h-[280px] object-cover transition-transform duration-1000 group-hover:scale-[1.02] img-enhance" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Divider */}
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="w-full h-[1px] bg-stone-200" />
        </div>

        {/* Property details */}
        <section className="max-w-3xl mx-auto px-6 md:px-12 py-16">
          <p className="text-[11px] tracking-[0.35em] uppercase text-stone-400 mb-8 font-medium">Details</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#2C5F8D] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Location</p>
                <p className="text-sm text-stone-700">{stay.region}, {stay.state}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 text-[#2C5F8D] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Best Time</p>
                <p className="text-sm text-stone-700">{getBestMonths(stay.best_months)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="w-4 h-4 text-[#2C5F8D] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Best For</p>
                <p className="text-sm text-stone-700">{stay.best_for?.map(b => b.replace('_', ' ')).join(', ') || 'Everyone'}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Closing Quote */}
        {closingLine && (
          <section className="max-w-3xl mx-auto px-6 md:px-12 py-16">
            <div className="w-12 h-[1px] bg-stone-300 mb-12" />
            <blockquote className="text-2xl md:text-3xl text-stone-700 italic leading-relaxed" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
              "{closingLine}"
            </blockquote>
          </section>
        )}

        {/* CTA */}
        <section className="bg-[#2C5F8D] py-20 px-6 md:px-12 text-center rounded-b-2xl" data-testid="cta-section">
          <div className="max-w-xl mx-auto">
            <div className="flex justify-center mb-8">
              <StayBadge partnershipStatus={stay.partnership_status} variant="cta" />
            </div>
            <h2 className="text-3xl md:text-4xl text-white font-light italic tracking-tight mb-3" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
              Stay at {displayName.split(',')[0]}
            </h2>
            {stay.starting_price_inr ? (
              <div className="mb-8" data-testid="cta-price">
                <p className="text-white/40 text-xs mb-1.5 tracking-wider uppercase">Starting from</p>
                <p className="text-white text-2xl md:text-3xl font-light italic tracking-tight" data-testid="starting-price" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
                  &#8377;{Number(stay.starting_price_inr).toLocaleString('en-IN')}
                  <span className="text-white/50 text-base font-normal">
                    {stay.price_unit === 'per_person' ? ' /person/night'
                      : stay.price_unit === 'whole_property' ? ' /night'
                      : ' /room/night'}
                  </span>
                </p>
                {(stay.includes_breakfast === true || (stay.price_includes && stay.price_includes.trim().toLowerCase() !== 'per night')) && (
                  <p className="text-white/40 text-xs mt-1 tracking-wider uppercase">
                    {stay.includes_breakfast === true ? 'Includes breakfast' : stay.price_includes}
                  </p>
                )}
              </div>
            ) : null}
            <p className="text-white/50 text-sm mb-10 max-w-sm mx-auto">
              Book directly with the property. Your enquiry connects you straight to the hosts for the best rates and a personalised experience.
            </p>
            <a
              href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                // Featured stays use the guided EnquireModal — structured dates / guests / rooms.
                e.preventDefault();
                setEnquireOpen(true);
              }}
              data-testid="whatsapp-cta"
              className="inline-flex items-center gap-3 bg-white text-[#2C5F8D] px-10 py-4 rounded-full text-sm font-medium hover:bg-white/90 transition-all hover:shadow-lg"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Check dates & enquire
            </a>
          </div>
        </section>
      </div>

      {/* Similar stays — "Stays you might also love" */}
      <SimilarStays currentStay={stay} />

      {/* Claim This Listing — supply-side conversion (hidden once owner has claimed
          OR when the stay is already a Featured/Partner — they've already opted in). */}
      {stay.claim_status !== "claimed"
        && stay.partnership_status !== "featured"
        && stay.partnership_status !== "partner"
        && stay.partnership_status !== "valley_partner" && (
      <div className="bg-[#faf8f4] border-t border-stone-200/60 py-14 px-6 md:px-12" data-testid="claim-listing-section">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-stone-400 text-[11px] tracking-[0.3em] uppercase mb-4">Property Owner?</p>
          <h3 className="text-xl md:text-2xl text-stone-700 font-light italic tracking-tight mb-3" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            Claim this listing
          </h3>
          <p className="text-stone-400 text-sm leading-relaxed mb-8 max-w-md mx-auto">
            This property was editorially discovered by GoHimalayan. If you're the owner, claim your listing to unlock premium visibility, direct bookings, and join the Collective.
          </p>
          <Link
            to="/partner"
            data-testid="claim-listing-cta"
            className="inline-flex items-center gap-2 border border-stone-300 text-stone-500 px-8 py-3 rounded-full text-xs font-medium tracking-[0.15em] uppercase hover:border-[#2C5F8D] hover:text-[#2C5F8D] transition-all"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
            Learn More
          </Link>
        </div>
      </div>
      )}

      {/* Footer */}
      <div className="bg-[#faf8f4] py-10 text-center">
        <Link to="/" className="text-stone-400 text-xs tracking-wider hover:text-stone-600 transition-colors uppercase">
          GoHimalayan &middot; Curated Himalayan Experiences
        </Link>
      </div>

      {/* Guided EnquireModal — live on all Featured stays */}
      <EnquireModal
        open={enquireOpen}
        onClose={() => setEnquireOpen(false)}
        stay={stay}
        whatsappNumber={whatsapp}
        startingPrice={stay?.starting_price_inr}
        heroImage={heroImage}
        source={stay?.partnership_status === 'partner' ? 'collective' : 'stay'}
        contextLabel="enquire_modal_bottom_cta"
        propertyShortName={displayName}
      />
    </div>
  );
}
